/* eslint-disable prettier/prettier */
import { SortMeta } from 'primeng/api';

import {
  GridConfig,
  GridLazyLoadEvent,
  SortingConfig,
  SortingDirection,
  SortingMode,
} from '../../models';
import { GridConfigProcessor } from './grid-config.processor';
import { createFilteringConfig } from './grid-filtering.helpers';
import { createPaginationConfig } from './grid-pagination.helpers';
import { createSortingConfig } from './grid-sorting.helpers';

export interface GridSortState {
  sortField: string | undefined;
  sortOrder: number;
  multiSortMeta: SortMeta[] | undefined;
}

export interface GridSortTrackingState {
  field: string;
  state: SortingDirection | null;
}

export const areSortingConfigsEqual = (
  previous: SortingConfig | undefined,
  next: SortingConfig | undefined,
): boolean => {
  if (previous?.mode !== next?.mode) return false;

  const previousRules = previous?.rules ?? [];
  const nextRules = next?.rules ?? [];

  return (
    previousRules.length === nextRules.length &&
    previousRules.every(
      (rule, index) =>
        rule.field === nextRules[index].field &&
        rule.direction === nextRules[index].direction,
    )
  );
};

export const toGridSortState = (
  sorting: SortingConfig | undefined,
): GridSortState => {
  const rules = sorting?.rules ?? [];
  const firstRule = rules[0];

  return {
    sortField:
      sorting?.mode === SortingMode.Single ? firstRule?.field : undefined,
    sortOrder:
      firstRule?.direction === SortingDirection.Descending ? -1 : 1,
    multiSortMeta:
      sorting?.mode === SortingMode.Multiple && rules.length
        ? rules.map(({ field, direction }) => ({
            field,
            order: direction === SortingDirection.Ascending ? 1 : -1,
          }))
        : undefined,
  };
};

export const toGridSortTrackingState = (
  sorting: SortingConfig | undefined,
): GridSortTrackingState => {
  const firstRule = sorting?.rules[0];

  return {
    field: firstRule?.field ?? '',
    state: firstRule?.direction ?? null,
  };
};

export const createGridConfig = (event: GridLazyLoadEvent): GridConfig => ({
  sorting: createSortingConfig(event),
  pagination: createPaginationConfig(event),
  filtering: createFilteringConfig(event),
});

export const applyGridConfig = <T extends Record<string, any>>(
  data: T[],
  config: GridConfig,
): T[] =>
  new GridConfigProcessor(data, config)
    .applyFilters()
    .applySorting()
    .applyPagination();

export const applyGridConfigWithoutPagination = <T extends Record<string, any>>(
  data: T[],
  config: GridConfig,
): T[] =>
  new GridConfigProcessor(data, config).applyFilters().applySorting().getData();

export const getNumberOfFilteredRecords = <T extends Record<string, any>>(
  data: T[],
  config: GridConfig,
): number =>
  new GridConfigProcessor(data, config).applyFilters().getData().length;
